import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Card } from "@/components/wpl/card/src/";
import { useLocalizedHref } from "@/libraries/web-framework";

const pageSection = css({
    marginInlineEnd: "auto",
    marginInlineStart: "auto",
    minWidth: "100%",
    padding: `${token("space.400")} ${token("space.0")}`,
    clear: "both",
    backgroundColor: "#deebff",
    position: "relative"
});

const resourcesMainContainer = css({
    display: "flex",
    maxWidth: "1200px",
    flexDirection: "column",
    margin: `${token("space.0")} auto ${token("space.0")} auto`
});

const resourcesHeader = css({
    textAlign: "center",
    h2: {
        color: "#253858",
        fontWeight: "500",
        padding: "16px",
        lineHeight: " 1.33333333",

        [media.below.sm]: {
            margin: "0 0 14px",
            fontSize: token("space.300")
        },

        [media.only.sm]: {
            margin: "0 0 14px",
            fontSize: "2.25rem"
        },

        [media.above.md]: {
            margin: "0 0 24px",
            fontSize: "2.25rem"
        }
    }
});

const cardsContainer = css({
    display: "flex",
    maxWidth: "1200px",

    [media.below.sm]: {
        flex: "12",
        flexDirection: "column"
    }
});

const resourcesCard = css({
    "flex": "4",
    "padding": token("space.200"),

    ".link-wrapper span": {
        fontWeight: "400"
    },

    ".text-section": {
        "margin": "0",

        "h3": {
            fontSize: "1.25rem",
            lineHeight: "1.2",

            [media.below.lg]: {
                marginBlockEnd: "14px"
            },

            [media.above.lg]: {
                marginBlockEnd: "24px"
            }
        },

        ".contentBlock-body": {
            marginBlockEnd: "24px"
        },

        ".contentBlock-linkFlex": {
            margin: "auto 0 0",
            a: {
                margin: "0"
            }
        }
    },

    [media.above.sm]: {
        ".text-section": {
            height: "100%"
        }
    }
});

const ResourcesSection = () => {
    const intl = useIntl();

    const cardData = [
        {
            imageLink:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_BlueDemoVideo.webp",
            imageAltText: intl.formatMessage({
                id: "confluence.section.resources.card0.image.alt",
                defaultMessage: "Demos"
            }),
            bodyText: intl.formatMessage({
                id: "confluence.section.resources.card0.body",
                defaultMessage:
                    "See the confluence in action and get a feel for basic"
            }),
            headerText: intl.formatMessage({
                id: "confluence.section.resources.card0.header",
                defaultMessage: "Demos"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "confluence.section.resources.card0.primary.link",
                    defaultMessage: "Explore demos"
                }),
                dataLabelEnglish: "Explore demos",
                link: useLocalizedHref("/software/confluence/demo")
            }
        },
        {
            imageLink:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_BlueUseCases.webp",
            imageAltText: intl.formatMessage({
                id: "confluence.section.resources.card1.image.alt",
                defaultMessage: "Use cases"
            }),
            bodyText: intl.formatMessage({
                id: "confluence.section.resources.card1.body",
                defaultMessage:
                    "Discover the ways Confluence can be shaped to meet your team’s needs"
            }),
            headerText: intl.formatMessage({
                id: "confluence.section.resources.card1.header",
                defaultMessage: "Use cases"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "confluence.section.resources.card1.primary.link",
                    defaultMessage: "Read the use cases"
                }),
                dataLabelEnglish: "Read the use cases",
                link: useLocalizedHref("/software/confluence/use-cases")
            }
        },
        {
            imageLink:
                "https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_BlueBestPractices.webp",
            imageAltText: intl.formatMessage({
                id: "confluence.section.resources.card2.image.alt",
                defaultMessage: "Best practices"
            }),
            bodyText: intl.formatMessage({
                id: "confluence.section.resources.card2.body",
                defaultMessage:
                    "Master techniques to get the most out of Confluence"
            }),
            headerText: intl.formatMessage({
                id: "confluence.section.resources.card2.header",
                defaultMessage: "Best practices"
            }),
            primaryLink: {
                text: intl.formatMessage({
                    id: "confluence.section.resources.card2.primary.link",
                    defaultMessage: "Go to Product Guides"
                }),
                dataLabelEnglish: "Go to Product Guides",
                link: useLocalizedHref("/software/confluence/resources/guides")
            }
        }
    ];
    return (
        <section>
            <div css={pageSection}>
                <div css={resourcesMainContainer}>
                    <div css={resourcesHeader}>
                        <h2>
                            <FormattedMessage
                                id="confluence.section.resources.header"
                                defaultMessage="Confluence resources for every step of the way"
                            />
                        </h2>
                    </div>

                    <div css={cardsContainer}>
                        {cardData.map((card, index) => (
                            <div css={resourcesCard} key={index}>
                                <Card
                                    key={index}
                                    imageLink={card.imageLink}
                                    imageAltText={card.imageAltText}
                                    bodyText={card.bodyText}
                                    headerText={card.headerText}
                                    aspectRatio="16/9"
                                    ctaProps={{
                                        primaryLinkProps: {
                                            type: "basic-underline",
                                            doesArrowAnimate: true,
                                            customLinkColor: "#0052cc",
                                            text: card.primaryLink.text,
                                            href: card.primaryLink.link,
                                            dataEventProps: {
                                                "data-product-key":
                                                    "confluence",
                                                "data-event-component":
                                                    "linkButton",
                                                "data-event-container":
                                                    "resourcesSection",
                                                "data-event": "clicked",
                                                "data-label":
                                                    card.primaryLink.text,
                                                "data-label-english":
                                                    card.primaryLink
                                                        .dataLabelEnglish
                                            }
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ResourcesSection;
